import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'

export interface SceneProps {
    active: boolean
}

export type HistoryItem = {
    id: number
    value: number
}

type Tone = 'default' | 'ember' | 'danger' | 'muted' | 'mint'

const TEXT_TONES: Record<Tone, string> = {
    default: 'text-bone',
    ember: 'text-ember',
    danger: 'text-danger',
    muted: 'text-steel',
    mint: 'text-mint'
}

const BADGE_TONES: Record<Tone, string> = {
    default: 'border-white/10 bg-white/[0.04] text-bone',
    ember: 'border-ember/40 bg-ember/10 text-ember',
    danger: 'border-danger/40 bg-danger/10 text-danger',
    muted: 'border-white/[0.08] bg-white/[0.02] text-steel',
    mint: 'border-mint/30 bg-mint/10 text-mint'
}

interface SceneLabelProps {
    children: ReactNode
    className?: string
}

interface SceneStatProps {
    label: string
    value: ReactNode
    tone?: Tone
    className?: string
}

interface SceneBadgeProps {
    children: ReactNode
    tone?: Tone
    className?: string
}

interface HistoryChipProps {
    children: ReactNode
    hot?: boolean
}

export const SceneLabel: FC<SceneLabelProps> = ({ children, className }) => {
    return (
        <span className={cn('font-mono text-[9px] uppercase tracking-[0.18em] text-steel/80', className)}>
            {children}
        </span>
    )
}

export const SceneStat: FC<SceneStatProps> = ({ label, value, tone = 'default', className }) => {
    return (
        <div className={cn('min-w-0', className)}>
            <SceneLabel>{label}</SceneLabel>
            <div className={cn('mt-1 truncate font-mono text-[13px] tabular-nums transition-colors duration-300', TEXT_TONES[tone])}>
                {value}
            </div>
        </div>
    )
}

export const SceneBadge: FC<SceneBadgeProps> = ({ children, tone = 'default', className }) => {
    return (
        <span className={cn('inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.12em] transition-colors duration-300', BADGE_TONES[tone], className)}>
            {children}
        </span>
    )
}

export const HistoryChip: FC<HistoryChipProps> = ({ children, hot = false }) => {
    return (
        <span className={cn('animate-in fade-in slide-in-from-left-2 rounded-md px-1.5 py-0.5 font-mono text-[10px] tabular-nums duration-300', hot ? 'bg-ember/15 text-ember' : 'bg-white/[0.05] text-steel')}>
            {children}
        </span>
    )
}

/** Reusable small panel used for read-only fields inside the scenes. */
export const SceneField: FC<SceneStatProps> = ({ label, value, tone = 'default', className }) => {
    return (
        <div className={cn('min-w-0 rounded-xl border border-white/[0.06] bg-white/[0.025] px-3 py-2', className)}>
            <SceneLabel>{label}</SceneLabel>
            <div className={cn('mt-0.5 truncate font-mono text-[12px] tabular-nums', TEXT_TONES[tone])}>
                {value}
            </div>
        </div>
    )
}
